import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  MessageSquare, 
  Rocket, 
  Target, 
  Users, 
  Zap, 
  Award,
  Calendar,
  ArrowRight,
  Menu,
  X,
  CheckCircle,
  TrendingUp,
  Globe,
  Shield,
  Sparkles,
  Heart,
  Code,
  Database,
  Smartphone,
  Mail,
  BarChart3,
  Lightbulb,
  Flag
} from 'lucide-react';

const Blog = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Overall company progress (percentage towards our 2025 goals)
  const overallProgress = 12;

  // Real stats as of January 2025
  const realStats = {
    totalUsers: 68,
    totalForms: 15,
    totalSubmissions: 20,
    launchDate: 'December 22, 2024',
    daysSinceLaunch: Math.floor((new Date().getTime() - new Date('2024-12-22').getTime()) / (1000 * 60 * 60 * 24))
  };

  const journeyPhases = [
    {
      phase: "Phase 1: The Idea",
      date: "November 2024",
      title: "The Birth of Bravax Forms",
      description: "It all started with a simple observation: Small business owners in Africa were struggling to manage customer data and lead collection across fragmented tools. We asked ourselves - what if there was a better way?",
      icon: Lightbulb,
      color: "bg-yellow-500",
      achievements: [
        "Identified the challenges African SMBs face in managing customer data",
        "Researched modern CRM and lead capture solutions",
        "Sketched the first wireframes of a simplified form-to-CRM solution",
        "Validated the idea with potential customers"
      ]
    },
    {
      phase: "Phase 2: Building",
      date: "December 2024",
      title: "From Concept to Reality",
      description: "In just weeks, we built the first version of Bravax Forms using modern technologies. On December 22, 2024, we launched our beta version - a simple yet powerful tool that lets anyone create smart forms that capture data and manage leads instantly.",
      icon: Code,
      color: "bg-blue-500",
      achievements: [
        "Built with React, TypeScript, and Supabase for scalability",
        "Developed drag-and-drop form builder",
        "Implemented smart delivery and lead capture logic",
        "Launched beta on December 22, 2024"
      ]
    },
    {
      phase: "Phase 3: Early Traction",
      date: "January 2025",
      title: "Our First Users",
      description: `Just ${realStats.daysSinceLaunch} days since launch and we already have ${realStats.totalUsers} registered users! Small business owners are discovering the power of automated lead generation. Every user teaches us something new.`,
      icon: Users,
      color: "bg-green-500",
      achievements: [
        `${realStats.totalUsers} users registered in first ${realStats.daysSinceLaunch} days`,
        `${realStats.totalForms} forms created by our community`,
        `${realStats.totalSubmissions} form submissions processed`,
        "Added custom branding and logo upload features",
        "Launched mini CRM for customer management"
      ]
    },
    {
      phase: "Phase 4: Current Focus",
      date: "Present",
      title: "Building for Scale",
      description: "We're focused on making Bravax Forms more powerful while keeping it simple. Every feature we add is based on real user feedback. We're preparing for our next growth phase.",
      icon: Zap,
      color: "bg-purple-500",
      achievements: [
        "Analytics dashboard for form performance tracking",
        "Advanced automation and follow-up templates",
        "Public API and embedding capabilities",
        "Unified Lead Management & CRM",
        "CRM import from HubSpot, Mailchimp, and more"
      ]
    },
    {
      phase: "Phase 5: The Vision",
      date: "2025 & Beyond",
      title: "Pan-African Expansion",
      description: "Our vision is to become the go-to platform for small business commerce across Africa. We're building features that will help millions of entrepreneurs connect with their customers more effectively.",
      icon: Flag,
      color: "bg-primary",
      achievements: [
        "Targeting 10,000 active businesses by end of 2025",
        "Expanding to 10+ African countries",
        "Multi-channel messaging (Telegram, Instagram, SMS)",
        "AI-powered customer insights",
        "Native mobile apps for iOS and Android"
      ]
    }
  ];

  const milestones = [
    {
      date: "November 2024",
      title: "Project Inception",
      description: "The idea for Bravax Forms was born from observing small business challenges with capturing and managing customer lead data.",
      icon: Lightbulb,
      metric: "Day 1"
    },
    {
      date: "December 22, 2024",
      title: "Official Launch",
      description: "Bravax Forms officially launched! We opened our doors to help entrepreneurs streamline form generation and lead capture.",
      icon: Rocket,
      metric: "🚀 Launch"
    },
    {
      date: "December 2024",
      title: "First Users",
      description: "Within days of launch, we welcomed our first users who started creating forms for their businesses.",
      icon: Users,
      metric: "First 10"
    },
    {
      date: "January 2025",
      title: `${realStats.totalUsers} Users Reached`,
      description: `In just ${realStats.daysSinceLaunch} days since launch, we've grown to ${realStats.totalUsers} registered users creating forms for their businesses.`,
      icon: TrendingUp,
      metric: `${realStats.totalUsers}+`
    },
    {
      date: "January 2025",
      title: "CRM & Analytics",
      description: "Launched our integrated mini CRM and analytics dashboard to help users manage customer relationships.",
      icon: Database,
      metric: "v2.0"
    },
    {
      date: "January 2025",
      title: "Smart Notifications",
      description: "Introduced automated notification templates for instant customer responses across multiple channels.",
      icon: MessageSquare,
      metric: "v2.5"
    },
    {
      date: "Coming Soon",
      title: "API & Embedding",
      description: "Launching public API and iframe embedding capabilities for developers and website integration.",
      icon: Code,
      metric: "API v1"
    },
    {
      date: "Q2 2025",
      title: "Mobile App",
      description: "Dedicated mobile apps for iOS and Android for on-the-go form and customer management.",
      icon: Smartphone,
      metric: "Planned"
    }
  ];

  const goals = [
    {
      title: "1,000 Active Users",
      description: "Help 1,000 small businesses and entrepreneurs streamline their lead capture and CRM by Q2 2025.",
      progress: Math.round((realStats.totalUsers / 1000) * 100),
      target: "1,000",
      current: `${realStats.totalUsers}`,
      icon: Users
    },
    {
      title: "Pan-African Expansion",
      description: "Expand our services across West Africa, East Africa, and Southern Africa by end of 2025.",
      progress: 10,
      target: "10 Countries",
      current: "1 Country (Nigeria)",
      icon: Globe
    },
    {
      title: "Multi-Channel Integration",
      description: "Integrate with Telegram, Facebook Messenger, and Instagram DM.",
      progress: 5,
      target: "4 Channels",
      current: "Multi-channel",
      icon: Smartphone
    },
    {
      title: "AI-Powered Insights",
      description: "Implement AI-driven analytics to help businesses understand customer behavior patterns.",
      progress: 15,
      target: "Full AI Suite",
      current: "Research Phase",
      icon: Sparkles
    },
    {
      title: "Payment Integration",
      description: "Native payment processing within forms for seamless checkout experiences.",
      progress: 20,
      target: "Full Payments",
      current: "In Development",
      icon: Zap
    },
    {
      title: "Mobile App Launch",
      description: "Launch dedicated mobile apps for iOS and Android for on-the-go management.",
      progress: 3,
      target: "iOS & Android",
      current: "Planning Phase",
      icon: Target
    }
  ];

  const values = [
    {
      icon: Heart,
      title: "Customer First",
      description: "Every feature we build starts with understanding our users' needs. We're building for small business owners, not enterprise corporations."
    },
    {
      icon: Zap,
      title: "Simplicity",
      description: "We believe in making technology accessible. No technical knowledge should be required to grow your business online."
    },
    {
      icon: Globe,
      title: "Local Focus",
      description: "Built for African entrepreneurs, understanding the unique challenges and opportunities of our markets."
    },
    {
      icon: Shield,
      title: "Trust & Security",
      description: "Your data and your customers' data are sacred to us. We maintain the highest security standards."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Our Journey & Vision | Bravax Forms Blog — AI Form Builder & CRM insights</title>
        <meta name="description" content="Follow the Bravax Forms journey - the leading AI form builder for African businesses. Discover our milestones, goals, and vision for the future of smart lead management." />
        <meta property="og:title" content="Our Journey & Vision | Bravax Forms Blog" />
        <meta property="og:description" content="Follow the Bravax Forms journey from launch to becoming the leading AI form builder." />
      </Helmet>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <Sparkles className="h-8 w-8 text-primary" />
              <div className="flex items-baseline gap-1">
                <span className="text-xl font-bold text-foreground">Bravax</span>
                <span className="text-sm text-foreground/70">Forms</span>
              </div>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="/" className="text-foreground hover:text-primary transition-colors">Home</a>
              <a href="/#features" className="text-foreground hover:text-primary transition-colors">Features</a>
              <a href="/#pricing" className="text-foreground hover:text-primary transition-colors">Pricing</a>
              <a href="/api-docs" className="text-foreground hover:text-primary transition-colors">API</a>
              <Button variant="ghost" onClick={() => navigate('/login')}>Sign In</Button>
              <Button className="btn-hero" onClick={() => navigate('/signup')}>Start Free Trial</Button>
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-4 border-t border-border">
              <a href="/" className="block text-foreground hover:text-primary transition-colors">Home</a>
              <a href="/#features" className="block text-foreground hover:text-primary transition-colors">Features</a>
              <a href="/#pricing" className="block text-foreground hover:text-primary transition-colors">Pricing</a>
              <a href="/api-docs" className="block text-foreground hover:text-primary transition-colors">API</a>
              <div className="space-y-2 pt-4 border-t border-border">
                <Button variant="ghost" className="w-full" onClick={() => navigate('/login')}>Sign In</Button>
                <Button className="w-full btn-hero" onClick={() => navigate('/signup')}>Start Free Trial</Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section with Overall Progress */}
      <section className="relative overflow-hidden py-16 sm:py-24 bg-gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="secondary" className="mb-6">Our Story</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Building the Future of
            <br />
            <span className="text-gradient">Smart Commerce</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            From a simple idea to serving hundreds of businesses across Africa. 
            Follow our journey as we transform how small businesses connect with customers.
          </p>
          
          {/* Overall Progress Card */}
          <div className="max-w-xl mx-auto">
            <Card className="bg-background/80 backdrop-blur border-primary/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">2025 Goals Progress</span>
                  <span className="text-2xl font-bold text-primary">{overallProgress}%</span>
                </div>
                <Progress value={overallProgress} className="h-3 mb-3" />
                <p className="text-sm text-muted-foreground">
                  We're {overallProgress}% of the way to our 2025 goals. Join us on this journey!
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Full Journey Story */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">The Full Story</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Our Journey From Day One</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Every great product starts with a problem worth solving. Here's how Bravax Forms came to be.
            </p>
          </div>

          <div className="space-y-12">
            {journeyPhases.map((phase, index) => (
              <div key={index} className="relative">
                {/* Connection Line */}
                {index !== journeyPhases.length - 1 && (
                  <div className="absolute left-6 top-20 bottom-0 w-0.5 bg-border hidden md:block" />
                )}
                
                <Card className="card-elegant overflow-hidden">
                  <div className="md:flex">
                    {/* Phase Header */}
                    <div className={`${phase.color} p-6 md:w-1/4 flex flex-col justify-center items-center text-white`}>
                      <phase.icon className="h-12 w-12 mb-3" />
                      <Badge variant="secondary" className="bg-white/20 text-white border-0 mb-2">
                        {phase.phase}
                      </Badge>
                      <span className="text-sm opacity-90">{phase.date}</span>
                    </div>
                    
                    {/* Phase Content */}
                    <div className="p-6 md:w-3/4">
                      <h3 className="text-2xl font-bold text-foreground mb-3">{phase.title}</h3>
                      <p className="text-muted-foreground mb-4">{phase.description}</p>
                      
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          Key Achievements
                        </h4>
                        <ul className="space-y-1">
                          {phase.achievements.map((achievement, achIndex) => (
                            <li key={achIndex} className="text-sm text-muted-foreground flex items-start gap-2">
                              <span className="text-primary mt-1">•</span>
                              {achievement}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 sm:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">What We Stand For</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our core values guide everything we build
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="card-elegant text-center">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{value.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones Timeline */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">Milestones</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Key Moments in Our Journey</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Celebrating the milestones that shaped Bravax Forms
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {milestones.map((milestone, index) => (
              <Card key={index} className="card-elegant hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <milestone.icon className="h-5 w-5 text-primary" />
                    </div>
                    <Badge variant="outline" className="text-xs">{milestone.metric}</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Calendar className="h-3 w-3" />
                    {milestone.date}
                  </div>
                  <CardTitle className="text-lg">{milestone.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{milestone.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Goals Section with Progress Bars */}
      <section className="py-16 sm:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">Looking Forward</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Our 2025 Goals</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Where we're headed and what we're building next
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {goals.map((goal, index) => (
              <Card key={index} className="card-elegant">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <goal.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary">{goal.progress}%</div>
                      <div className="text-xs text-muted-foreground">completed</div>
                    </div>
                  </div>
                  <CardTitle className="text-lg">{goal.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CardDescription>{goal.description}</CardDescription>
                  
                  <div className="space-y-2">
                    <Progress value={goal.progress} className="h-2" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Current: {goal.current}</span>
                      <span>Target: {goal.target}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-gradient-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Join Us on This Journey
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Be part of the growing community of businesses transforming their customer interactions with Bravax Forms.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="text-lg px-8" onClick={() => navigate('/signup')}>
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8 bg-transparent border-white text-white hover:bg-white/10" onClick={() => window.location.href = 'mailto:bravaxforms@gmail.com'}>
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Sparkles className="h-6 w-6 text-primary" />
              <span className="text-lg font-bold text-foreground">Bravax Forms</span>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
              <a href="/" className="hover:text-primary transition-colors">Home</a>
              <a href="/#features" className="hover:text-primary transition-colors">Features</a>
              <a href="/#pricing" className="hover:text-primary transition-colors">Pricing</a>
              <a href="/api-docs" className="hover:text-primary transition-colors">API</a>
              <a href="mailto:bravaxforms@gmail.com" className="hover:text-primary transition-colors">Contact</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>© 2025 Bravax Forms. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;